import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import apiService from '../../Shared/services/api';
import type { User } from '../../Shared/types';

// Re-export User type for tests
export type { User };

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: User) => void;
  logout: () => void;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// SPA cookie auth keeps no token in JS, so this flag records that a login
// established a session. It's used by the API client to decide whether a 401
// should bounce the user to /login. The flag is NOT used to skip the initial
// /user probe — a valid cookie session must always be able to restore auth.
export const AUTH_SESSION_FLAG = 'numbie_authenticated';

export const AuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const initializeAuth = async (): Promise<void> => {
      // Always probe /user once on mount. With SPA cookie auth there's no token
      // in JS, so a valid session cookie (new tab, cleared localStorage) must
      // still restore the session. A 401 just means guest — swallow it.
      try {
        const userData = await apiService.getUser();
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem(AUTH_SESSION_FLAG, '1');
      } catch {
        localStorage.removeItem(AUTH_SESSION_FLAG);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    void initializeAuth();
  }, []);

  const login = (userData: User): void => {
    localStorage.setItem(AUTH_SESSION_FLAG, '1');
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = (): void => {
    localStorage.removeItem(AUTH_SESSION_FLAG);
    void apiService.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
